"use client";
import { useEffect, useRef, useState } from "react";
import styles from "./index.module.css";
import { useTemplateComponentClassNames } from "../store/template/template-style-context";

const mergeClassNames = (...classNames) => classNames.filter(Boolean).join(" ");

function Dropdown({
  options = [],
  value,
  onSelect,
  getOptionValue = (opt) => opt.value,
  getOptionLabel = (opt) => opt.label,
  className = "",
  listClassName = "",
  themeKey = "",
  classNames = {},
  children,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropDownRef = useRef();
  const [openDirection, setOpenDirection] = useState("below");
  const [iconDirection, setIconDirection] = useState("down");
  const themeClassNames = useTemplateComponentClassNames("dropdown", themeKey);
  const resolvedClassNames = {
    root: mergeClassNames(themeClassNames.root, classNames.root),
    trigger: mergeClassNames(themeClassNames.trigger, classNames.trigger),
    button: mergeClassNames(themeClassNames.button, classNames.button),
    text: mergeClassNames(themeClassNames.text, classNames.text),
    list: mergeClassNames(themeClassNames.list, classNames.list),
    item: mergeClassNames(themeClassNames.item, classNames.item),
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  useEffect(() => {
    if (dropDownRef.current) {
      const rect = dropDownRef.current.getBoundingClientRect();
      const buttonY = rect.top + rect.height / 2;
      const halfScreen = window.innerHeight / 2;
      setIconDirection(buttonY > halfScreen ? "up" : "down");
    }
  }, []);

  const toggleDropdown = () => {
    setShowDropdown((prev) => {
      const next = !prev;

      if (next && dropDownRef.current) {
        const rect = dropDownRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceAbove > spaceBelow) {
          setOpenDirection("above");
          setIconDirection("up");
        } else {
          setOpenDirection("below");
          setIconDirection("down");
        }
      }

      return next;
    });
  };

  if (options.length <= 0) return null;

  const selected = options.find((opt) => getOptionValue(opt) === value);

  return (
    <div
      className={mergeClassNames(styles.dropDownCase, resolvedClassNames.root, className)}
      ref={dropDownRef}
    >
      <div
        onClick={toggleDropdown}
        className={mergeClassNames(styles.trigger, resolvedClassNames.trigger)}
      >
        {children ? (
          children
        ) : (
          <button className={mergeClassNames(styles.toggleButton, resolvedClassNames.button)}>
            <span className={mergeClassNames(styles.buttonText, resolvedClassNames.text)}>
              {selected ? getOptionLabel(selected) : "Select..."}
              {iconDirection === "down" ? (
                <svg width={17} height={17} viewBox="0 0 24 24">
                  <path
                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                  />
                </svg>
              ) : (
                <svg width={17} height={17} viewBox="0 0 24 24">
                  <path
                    d="m19.5 15.75-7.5-7.5-7.5 7.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                  />
                </svg>
              )}
            </span>
          </button>
        )}
      </div>

      <ul
        className={mergeClassNames(
          styles.dropDownList,
          showDropdown ? styles.show : "",
          styles[openDirection],
          resolvedClassNames.list,
          listClassName,
        )}
      >
        {options.map((opt) => (
          <li
            key={getOptionValue(opt)}
            className={mergeClassNames(styles.dropDownItem, resolvedClassNames.item)}
            onClick={() => {
              onSelect(opt);
              setShowDropdown(false);
            }}
          >
            {getOptionLabel(opt)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Dropdown;
